/**
 * DriveContext — separate from user auth. Tracks whether Google Drive is
 * connected for the price-list feature. Uses user auth token via the shared
 * `api` instance (interceptor).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "./AuthContext";

const DriveContext = createContext(null);

export function DriveProvider({ children }) {
  const { status } = useAuth();
  const [connected, setConnected] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    if (status !== "authed") {
      setChecking(false);
      setConnected(false);
      return;
    }
    try {
      const { data } = await api.get("/drive/status");
      setConnected(!!data.connected);
    } catch (e) {
      console.error("drive status failed", e);
      setConnected(false);
    } finally {
      setChecking(false);
    }
  }, [status]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(() => {
    // Needs to open /api/drive/connect with the bearer token; simplest is
    // to include token as query param or hit the backend via full-page navigation.
    // Our /drive/connect just redirects to Google, no auth needed on the backend
    // for OAuth bootstrap — but server does require auth via header. We open a
    // window that passes the token in the URL via a small helper endpoint.
    // Simpler: full-page nav with Authorization won't work; so we mark /drive/connect
    // public on the server. Already done — see server.py (drive router not guarded).
    window.location.href = `${API_BASE}/drive/connect`;
  }, []);

  const disconnect = useCallback(async () => {
    await api.post("/drive/disconnect");
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ connected, checking, refresh, login, disconnect }),
    [connected, checking, refresh, login, disconnect]
  );

  return <DriveContext.Provider value={value}>{children}</DriveContext.Provider>;
}

export function useDrive() {
  const ctx = useContext(DriveContext);
  if (!ctx) throw new Error("useDrive outside DriveProvider");
  return ctx;
}
